/**
 * Where a concept came from — ROUTE_INVENTORY §2.10 #44.
 *
 * Extracted from `ConceptDetailSidebar`. Two lists under one heading, because
 * from the reader's point of view "which lesson taught me this" and "which
 * document did it come out of" are the same question.
 *
 * ## A deleted lesson is inert, not hidden
 *
 * The backend signals a vanished lesson with `lesson_title === null` rather
 * than a sentinel string, and the row renders as struck-through "(deleted)"
 * with **no button**. Both halves matter. Dropping the row entirely would make
 * a concept look like it came from nowhere; keeping it clickable would offer a
 * navigation that can only 404. It is the one row in the panel that is
 * deliberately not interactive, and that is the assertion worth having.
 *
 * ## "Bytes", not "excerpts"
 *
 * `excerpt_count` is rendered as "3 bytes". That reads like a file size and is
 * not one — it is the product's own term, and the API schema uses it too
 * ("Uploaded source files that contributed bytes to this concept"). Left
 * alone deliberately; renaming it here would put the sidebar at odds with
 * Quick Bytes, which is the surface those excerpts are shown on.
 */

import {
  BookOpen,
  ExternalLink,
  FileText,
  Globe,
  Notebook,
} from 'lucide-react';
import {
  PANEL_ROW,
  PANEL_ROW_INTERACTIVE,
  PanelSection,
} from '@/components/features/mindspace/concepts/PanelSection';
import type { ConceptDetail } from '@/types/mindspace-concept';
import { parseUtcDate } from '@/lib/datetime';

interface ConceptSourcesSectionProps {
  lessons: ConceptDetail['source_lessons'];
  documents: NonNullable<ConceptDetail['source_documents']>;
  onNavigateToLesson: (lessonId: string) => void;
}

export function ConceptSourcesSection({
  lessons,
  documents,
  onNavigateToLesson,
}: ConceptSourcesSectionProps) {
  if (lessons.length === 0 && documents.length === 0) return null;

  return (
    <PanelSection
      icon={<BookOpen className='h-3.5 w-3.5' aria-hidden />}
      title='Where it came from'
    >
      {lessons.length > 0 && (
        <div className='mb-2 last:mb-0'>
          <p className='px-1.5 pb-0.5 text-xs font-medium text-muted-foreground'>
            Lessons ({lessons.length})
          </p>
          <ul>
            {lessons.map((lesson, idx) => (
              <SourceLessonRow
                key={`${lesson.lesson_id}-${idx}`}
                lesson={lesson}
                onNavigate={onNavigateToLesson}
              />
            ))}
          </ul>
        </div>
      )}

      {documents.length > 0 && (
        <div>
          <p className='px-1.5 pb-0.5 text-xs font-medium text-muted-foreground'>
            Documents ({documents.length})
          </p>
          <ul>
            {documents.map(doc => (
              <SourceDocumentRow key={doc.id} doc={doc} />
            ))}
          </ul>
        </div>
      )}
    </PanelSection>
  );
}

function SourceLessonRow({
  lesson,
  onNavigate,
}: {
  lesson: ConceptDetail['source_lessons'][number];
  onNavigate: (lessonId: string) => void;
}) {
  const isDeleted = !lesson.lesson_title;

  if (isDeleted) {
    return (
      <li className={PANEL_ROW}>
        <span
          className='inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40'
          aria-hidden
        />
        <span className='italic text-muted-foreground line-through'>
          (deleted)
        </span>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={() => onNavigate(lesson.lesson_id)}
        className={PANEL_ROW_INTERACTIVE}
      >
        <span
          className='inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary'
          aria-hidden
        />
        <span className='min-w-0 flex-1 truncate font-medium'>
          {lesson.lesson_title}
        </span>
        {lesson.learned_at && (
          <span className='flex-shrink-0 text-xs text-muted-foreground'>
            {parseUtcDate(lesson.learned_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        )}
      </button>
    </li>
  );
}

function SourceDocumentRow({
  doc,
}: {
  doc: NonNullable<ConceptDetail['source_documents']>[number];
}) {
  const openUrl = doc.open_url || null;
  const count = doc.excerpt_count;
  const content = (
    <>
      <SourceTypeIcon type={doc.source_type} />
      <span className='min-w-0 flex-1 truncate'>{doc.filename}</span>
      {count != null && count > 0 && (
        // Singular matters and is easy to miss: a document contributing one
        // excerpt read "1 bytes" for as long as this panel has existed.
        <span className='flex-shrink-0 text-xs tabular-nums text-muted-foreground'>
          {count} {count === 1 ? 'byte' : 'bytes'}
        </span>
      )}
      {openUrl && (
        <ExternalLink
          className='h-3 w-3 flex-shrink-0 text-muted-foreground'
          aria-hidden
        />
      )}
    </>
  );

  return (
    <li>
      {openUrl ? (
        <a
          href={openUrl}
          target='_blank'
          rel='noopener noreferrer'
          className={PANEL_ROW_INTERACTIVE}
          title='Open original in a new tab'
        >
          {content}
        </a>
      ) : (
        // No link to give — an uploaded file exposes only an S3 key, which the
        // frontend cannot sign. The row still renders, because "this document
        // contributed" is worth knowing even when it cannot be opened.
        <div className={PANEL_ROW}>{content}</div>
      )}
    </li>
  );
}

function SourceTypeIcon({ type }: { type: string }) {
  const t = type.toLowerCase();
  if (t === 'link' || t === 'url' || t === 'web') {
    return <Globe className='h-3.5 w-3.5 text-muted-foreground' aria-hidden />;
  }
  if (t === 'text' || t === 'note' || t === 'notes') {
    return (
      <Notebook className='h-3.5 w-3.5 text-muted-foreground' aria-hidden />
    );
  }
  return <FileText className='h-3.5 w-3.5 text-muted-foreground' aria-hidden />;
}
